/**
 * ARCHX AI — Export Gate Service
 *
 * Controls STL file delivery after design acceptance.
 *
 * BETA_MODE = true  → file releases immediately, zero friction
 * BETA_MODE = false → Stripe payment required before file releases (Month 4)
 *
 * Every export is logged in Firestore regardless of mode.
 * Dereck receives an email notification for every export.
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

// ── Flip this to false when you're ready to charge ──────────────────────────
const BETA_MODE = true;

const NOTIFY_URL = process.env.REACT_APP_WORKER_URL || 'https://archxai.io';

/**
 * Request an STL export for a completed case.
 * Logs the export, sends notification email, returns download URL.
 *
 * @param {Object} caseData   - Full case document from Firestore
 * @param {Object} user       - Firebase auth user
 * @returns {Promise<string>} - Download URL for the STL file
 */
export async function requestExport(caseData, user) {
  const { id: caseDocId, caseNumber, patientFirst, patientLast, practiceId } = caseData;

  // 1. Log export request in Firestore
  await addDoc(collection(db, 'exports'), {
    caseDocId,
    caseNumber,
    patientName:  `${patientFirst} ${patientLast}`,
    practiceId,
    clinicianEmail: user.email,
    betaMode:     BETA_MODE,
    status:       BETA_MODE ? 'released' : 'pending_payment',
    requestedAt:  serverTimestamp(),
  });

  // 2. Send notification email to Dereck
  try {
    await fetch(`${NOTIFY_URL}/api/export-notify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        caseNumber,
        patientName:    `${patientFirst} ${patientLast}`,
        clinicianEmail: user.email,
        betaMode:       BETA_MODE,
      }),
    });
  } catch (e) {
    // Non-blocking — log failure but don't block the download
    console.warn('Export notification failed:', e.message);
  }

  // 3. In beta: release the file immediately
  if (BETA_MODE) {
    return await getSTLDownloadURL(caseData);
  }

  // 4. Post-beta: Stripe payment gate (Month 4)
  // const session = await createStripeCheckout(caseData, user);
  // window.location.href = session.url;
  throw new Error('Payment gate not yet active — beta mode is off but Stripe is not configured');
}

/**
 * Get the Firebase Storage download URL for the provisional STL.
 * The engine saves the STL at: cases/{practiceId}/{caseNumber}/output/provisional.stl
 */
async function getSTLDownloadURL(caseData) {
  const { practiceId, caseNumber } = caseData;

  // Check for engine-generated output first
  const enginePath = `cases/${practiceId}/${caseNumber}/output/provisional.stl`;
  try {
    const url = await getDownloadURL(ref(storage, enginePath));
    return url;
  } catch {
    // Fall back to any uploaded STL aligned file
    const uploadedSTL = caseData.files?.find(
      f => f.category === 'stlAligned' || f.category === 'stlProvisional'
    );
    if (uploadedSTL) {
      return await getDownloadURL(ref(storage, uploadedSTL.path));
    }
    throw new Error('No STL file available for this case yet — engine processing may still be in progress');
  }
}
